import React from 'react';
import { splitExtension } from '~/lib/phone';
import { tidyPhone } from '~/components/csv-import/model/parse';
import { CSV_LIST_SEPARATOR, splitList, type CsvField } from '~/components/csv-import/model/schema';

/**
 * The editor that opens over a clipped grid cell, wider than the column so the full
 * value can be read. Placement belongs to the enclosing panel; this picks the control
 * for the field, e.g. a picker for a set of allowed values.
 */

/** Characters a field accepts while being typed, where it accepts only some. */
const allowedWhileTyping = (field: CsvField): RegExp | undefined => {
  if (field.allow) {
    return field.allow;
  }
  return field.type === 'phone' ? /[\d+ ().-]/ : undefined;
};

const keepAllowed = (text: string, allow: RegExp | undefined) =>
  allow ? [...text].filter((char) => allow.test(char)).join('') : text;

/** The extension markers libphonenumber reads, e.g. "x204", "ext. 204", "#204" or ",204". */
const EXTENSION_MARKER = /(?:;ext=|ext(?:ension|n)?\.?|x|#|,)\s*(\d+)#?\s*$/i;

const OptionList = ({
  field,
  value,
  onChange,
  onClose
}: {
  field: CsvField;
  value: string;
  onChange: (next: string) => void;
  onClose: () => void;
}) => {
  const chosen = new Set(splitList(value));

  const toggle = (option: string) => {
    if (!field.multiple) {
      // Re-picking the current value keeps it; a blank can mean a default, not "none".
      onChange(option);
      onClose();
      return;
    }
    const next = new Set(chosen);
    if (!next.delete(option)) {
      next.add(option);
    }
    // Schema order, not click order, so the same choices always read the same.
    onChange(
      (field.enumValues ?? []).filter((option_) => next.has(option_)).join(CSV_LIST_SEPARATOR)
    );
  };

  return (
    <ul className="max-h-56 overflow-y-auto">
      {(field.enumValues ?? []).map((option) => (
        <li key={option}>
          <button
            type="button"
            aria-pressed={chosen.has(option)}
            onClick={() => toggle(option)}
            className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-neutral-100 dark:hover:bg-neutral-700"
          >
            <span
              aria-hidden
              className={`inline-block w-3 shrink-0 text-sky-600 dark:text-sky-400 ${
                chosen.has(option) ? '' : 'opacity-0'
              }`}
            >
              ✓
            </span>
            <span className="truncate text-neutral-800 dark:text-neutral-100">{option}</span>
          </button>
        </li>
      ))}
    </ul>
  );
};

export const CsvCellEditor = ({
  field,
  value,
  onChange,
  onClose,
  onLeave,
  extensionLabel
}: {
  field: CsvField;
  value: string;
  onChange: (next: string) => void;
  onClose: () => void;
  /** Called as the editor is left after an edit, once any tidying has been handed over. */
  onLeave?: () => void;
  /** For a phone field: the label of the column its extension goes in. */
  extensionLabel?: string;
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isPhone = field.type === 'phone';
  // Extension dropped from the last paste, named in the hint.
  const [droppedExtension, setDroppedExtension] = React.useState<string>();

  // Later `value` changes are ignored: the editor mounts afresh each time it opens.
  const [text, setText] = React.useState(value);
  // Also a ref: an option pick closes the editor before `text` re-renders.
  const draft = React.useRef(value);
  const edited = React.useRef(false);

  const latest = React.useRef({ onChange, onLeave });
  latest.current = { onChange, onLeave };
  /**
   * Tidies the value and reports leaving, once per stretch of edits; an untouched
   * editor does nothing. Runs on blur and on unmount, since browsers fire no blur
   * for a focused element that is removed.
   */
  const leave = React.useCallback(() => {
    if (!edited.current) {
      return;
    }
    edited.current = false;
    const current = draft.current;
    const trimmed = current.trim();
    const tidy = isPhone ? tidyPhone(trimmed) : trimmed;
    if (tidy !== current) {
      draft.current = tidy;
      setText(tidy);
      latest.current.onChange(tidy);
    }
    latest.current.onLeave?.();
  }, [isPhone]);
  React.useEffect(() => leave, [leave]);

  const edit = (next: string) => {
    edited.current = true;
    draft.current = next;
    setText(next);
    onChange(next);
  };

  // Not autoFocus: revealing the input scrolls every ancestor, throwing the grid to row one.
  React.useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  const isOptions = field.type === 'enum' && (field.enumValues?.length ?? 0) > 0;
  const allow = allowedWhileTyping(field);

  return (
    <>
      <div className="mb-1 px-1 text-[10px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
        {field.label}
      </div>

      {isOptions ? (
        <OptionList
          field={field}
          value={value}
          // A choice is the option list's typing: the cell is judged once the list closes.
          onChange={edit}
          onClose={onClose}
        />
      ) : (
        <>
          <input
            ref={inputRef}
            value={text}
            aria-label={field.label}
            inputMode={isPhone ? 'tel' : field.type === 'number' ? 'numeric' : undefined}
            // Canonicalised on blur so the number is not rewritten under the cursor.
            onBlur={leave}
            onPaste={(event) => {
              if (!isPhone) {
                return;
              }
              // Dropping an extension's letters would merge its digits into the number.
              const pasted = event.clipboardData.getData('text');
              const marker = EXTENSION_MARKER.exec(pasted);
              // A number that doesn't parse keeps what was written before the marker.
              const split =
                splitExtension(pasted) ??
                (marker && {
                  number: keepAllowed(pasted.slice(0, marker.index), allow),
                  extension: marker[1]
                });
              setDroppedExtension(split?.extension);
              if (split) {
                event.preventDefault();
                edit(split.number);
              }
            }}
            onChange={(event) => {
              setDroppedExtension(undefined);
              edit(keepAllowed(event.currentTarget.value, allow));
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                // The grid can sit inside a form, which Enter would otherwise submit.
                event.preventDefault();
                onClose();
              }
            }}
            className="w-full rounded border border-neutral-200 bg-transparent px-2 py-1 text-xs text-neutral-900 dark:border-neutral-600 dark:text-white"
          />
          {isPhone &&
            (droppedExtension ? (
              <p className="mt-1 px-1 text-[10px] text-amber-700 dark:text-amber-300">
                Extension {droppedExtension} wasn't included.{' '}
                {extensionLabel
                  ? `Add it in the ${extensionLabel} column.`
                  : 'This column holds only the number.'}
              </p>
            ) : (
              <p className="mt-1 px-1 text-[10px] text-neutral-500 dark:text-neutral-400">
                A US number, or + and the country code for others.
                {extensionLabel && ` Put any extension in the ${extensionLabel} column.`}
              </p>
            ))}
        </>
      )}
    </>
  );
};
